import React, { useState } from "react";
import { PhoneFormStep } from "../../components/client/PhoneFormStep";
import { ConfirmDataStep } from "../../components/client/ConfirmDataStep";
import { NewClientFormStep } from "../../components/client/NewClientFormStep";
import { saveClientSession } from "../../utils/clientSession";
import { recordClientAccess } from "../../services/clientAccessLogService";

enum Step {
  PHONE = "PHONE",
  CONFIRM = "CONFIRM",
  CREATE = "CREATE",
}

export const ClientRegistrationView: React.FC = () => {
  const [step, setStep] = useState<Step>(Step.PHONE);
  const [phone, setPhone] = useState<string>("");
  const [existingLead, setExistingLead] = useState<{ id: string; name: string; phone: string; tags: any[] } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const redirectToClientAccess = () => {
    window.history.replaceState(null, '', '/accesocliente');
    window.dispatchEvent(new Event('popstate'));
  };

  const handlePhoneSubmit = async (enteredPhone: string) => {
    setPhone(enteredPhone);
    setIsLoading(true);
    setErrorMsg(null);

    try {
      const res = await fetch(`/api/client-register/search?phone=${encodeURIComponent(enteredPhone)}`);
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || "No fue posible verificar el teléfono.");
      }

      if (data.exists && data.lead) {
        setExistingLead(data.lead);
        setStep(Step.CONFIRM);
      } else {
        setExistingLead(null);
        setStep(Step.CREATE);
      }
    } catch (err: any) {
      setErrorMsg(err.message || "Ocurrió un error al buscar tus datos.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmLead = async () => {
    if (!existingLead) return;
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const res = await fetch("/api/client-register/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadId: existingLead.id, currentTags: existingLead.tags || [] }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Error al actualizar los datos en Zoho.");
      const sessionData = { clientId: existingLead.id, nombre: existingLead.name, telefono: phone };
      saveClientSession(sessionData);
      await recordClientAccess(sessionData);
      redirectToClientAccess();
    } catch (err: any) {
      setErrorMsg(err.message || "Error al confirmar el acceso.");
      setIsLoading(false);
    }
  };

  const handleCreateLead = async (name: string) => {
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const res = await fetch("/api/client-register/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, name }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Error al crear el registro en Zoho.");
      const newId = data.data?.id || `lead-${Date.now()}`;
      const sessionData = { clientId: newId, nombre: name, telefono: phone };
      saveClientSession(sessionData);
      await recordClientAccess(sessionData);
      redirectToClientAccess();
    } catch (err: any) {
      setErrorMsg(err.message || "Error al registrar tus datos.");
      setIsLoading(false);
    }
  };

  return (
    <div id="client-registration-container" className="min-h-screen bg-gradient-to-b from-blue-50/60 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md px-4">
        <div className="bg-white py-8 px-6 sm:px-10 shadow-xl rounded-2xl border border-gray-100">
          {errorMsg && (
            <div className="mb-6 p-3.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {errorMsg}
            </div>
          )}

          {step === Step.PHONE && (
            <PhoneFormStep initialPhone={phone} onSubmit={handlePhoneSubmit} isLoading={isLoading} />
          )}
          {step === Step.CONFIRM && existingLead && (
            <ConfirmDataStep
              name={existingLead.name}
              phone={existingLead.phone || phone}
              onConfirm={handleConfirmLead}
              onReject={() => setStep(Step.PHONE)}
              isLoading={isLoading}
            />
          )}
          {step === Step.CREATE && (
            <NewClientFormStep
              phone={phone}
              onSubmit={handleCreateLead}
              onBack={() => setStep(Step.PHONE)}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};
